/**
 * Milestones — project-level deadlines. Tasks can belong to at most one
 * milestone. Mirrors the sprints feature module: list, create, archive.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Milestone, Task } from '@friday/shared/realtime'

interface MilestoneCreateInput {
  name: string
  description?: string
  dueDate?: string | null
  color?: string
}

export function useMilestones(wsId: string, pId: string | undefined) {
  return useQuery({
    queryKey: ['milestones', pId],
    queryFn: () => api<Milestone[]>(`/api/v1/projects/${pId}/milestones`),
    enabled: !!pId,
  })
}

export function useCreateMilestone(wsId: string, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MilestoneCreateInput) =>
      api<Milestone>(`/api/v1/projects/${pId}/milestones`, {
        method: 'POST',
        body: input,
        idemKey: crypto.randomUUID(),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['milestones', pId] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['workspace-milestones', wsId] })
    },
  })
}

export function useUpdateMilestone(wsId: string, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<MilestoneCreateInput & { status: Milestone['status'] }> }) =>
      api<Milestone>(`/api/v1/milestones/${id}`, { method: 'PATCH', body: patch, idemKey: crypto.randomUUID() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['milestones', pId] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['workspace-milestones', wsId] })
    },
  })
}

export function useArchiveMilestone(wsId: string, pId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/api/v1/milestones/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['milestones', pId] })
      void qc.invalidateQueries({ queryKey: ['board', wsId, pId] })
      void qc.invalidateQueries({ queryKey: ['workspace-milestones', wsId] })
    },
  })
}

// ────────────── Workspace aggregation ──────────────
// Milestones stay board-owned; this only lists the ones belonging to boards
// the caller may read, each labelled with its owning board.

export interface WorkspaceMilestone {
  id: string
  projectId: string
  name: string
  description: string | null
  dueDate: string | null
  status: Milestone['status']
  color: string
  version: number
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  /** The board that owns the milestone. */
  projectKey: string
  projectName: string
  /** Non-archived tasks attached to the milestone. */
  taskCount: number
  /** How many of them are done. */
  doneCount: number
}

export interface WorkspaceMilestoneBoard {
  id: string
  key: string
  name: string
  archivedAt: string | null
  /** Whether the caller may write to this board. */
  canWrite: boolean
}

export interface WorkspaceMilestonesPayload {
  milestones: WorkspaceMilestone[]
  boards: WorkspaceMilestoneBoard[]
  total: number
  limit: number
  offset: number
}

export interface WorkspaceMilestonesQuery {
  q?: string
  projectId?: string
  includeArchived?: boolean
  limit?: number
  offset?: number
}

/** Every milestone of every board the caller may read. */
export function useWorkspaceMilestones(
  wsId: string | undefined,
  params: WorkspaceMilestonesQuery = {},
) {
  const { q, projectId, includeArchived, limit, offset } = params
  return useQuery({
    queryKey: [
      'workspace-milestones',
      wsId,
      {
        q: q ?? '',
        projectId: projectId ?? '',
        includeArchived: !!includeArchived,
        limit: limit ?? 100,
        offset: offset ?? 0,
      },
    ],
    queryFn: () =>
      api<WorkspaceMilestonesPayload>(`/api/v1/workspaces/${wsId}/milestones`, {
        query: {
          q: q || undefined,
          projectId: projectId || undefined,
          includeArchived: includeArchived ? 'true' : undefined,
          limit: limit ?? 100,
          offset: offset ?? 0,
        },
      }),
    enabled: !!wsId,
  })
}

export interface MilestoneProgress {
  milestoneId: string
  total: number
  done: number
  ratio: number
}

/** Group tasks by milestone and compute progress. */
export function computeMilestoneProgress(tasks: Task[]): Map<string, MilestoneProgress> {
  const map = new Map<string, MilestoneProgress>()
  for (const t of tasks) {
    if (!t.milestoneId || t.parentTaskId) continue
    const cur = map.get(t.milestoneId) ?? { milestoneId: t.milestoneId, total: 0, done: 0, ratio: 0 }
    cur.total += 1
    if (t.status === 'done') cur.done += 1
    cur.ratio = cur.total > 0 ? cur.done / cur.total : 0
    map.set(t.milestoneId, cur)
  }
  return map
}

export function formatDueDate(iso: string | null): string {
  if (!iso) return 'No due date'
  const d = new Date(iso)
  const now = new Date()
  const days = Math.round((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000))
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  if (days < 0) return `Overdue · ${d.toLocaleDateString(undefined, opts)}`
  if (days === 0) return 'Due today'
  if (days === 1) return 'Due tomorrow'
  if (days < 14) return `Due in ${days} days · ${d.toLocaleDateString(undefined, opts)}`
  return `Due ${d.toLocaleDateString(undefined, opts)}`
}